import React, { useState, useEffect, createContext } from 'react';
import { useLocation } from '@reach/router';
import PropTypes from 'prop-types';
import Navigation from '../2_sections/Navigation';
import Footer from '../2_sections/Footer';
import TransitionPageIn from '../0_transitions/TransitionPageIn.js';
import ScrollToTopButton from '../1_components/ScrollToTopButton';
import ErrorBoundary from '../1_components/ErrorBoundary';

export const DarkThemeContext = createContext();

const useDarkMode = () => {
   const location = useLocation();
   const pathname = location?.pathname || (typeof window !== 'undefined' ? window.location.pathname : '/');
   const [darkMode, setDarkMode] = useState(true);

   useEffect(() => {
      setDarkMode(!['/project/', '/article/'].some((path) => pathname.startsWith(path)));
   }, [pathname]);

   const toggleDarkMode = () => {
      setDarkMode(!darkMode);
   };

   return { darkMode, toggleDarkMode, pathname };
};

const ScrollToTopContainer = () => (
   <div className="fixed right-[6%] bottom-[8%] flex space-x-2 p-7 pr-0 pb-0">
      <ScrollToTopButton />
   </div>
);

const Layout = ({ children }) => {
   const { darkMode, toggleDarkMode, pathname } = useDarkMode();
   const isLightBackground = pathname.includes('/project/') || pathname.includes('/article/');

   const containerClasses = `
      ${darkMode && 'dark'}
      ${isLightBackground ? 'bg-background--bright' : 'bg-background--dark'}
      relative h-full w-full overflow-hidden
   `;

   return (
      <DarkThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
         <ErrorBoundary>
            <div className={containerClasses}>
               {/* Skip to main content link for accessibility */}
               <a
                  href="#main-content"
                  className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-surface-1 focus:text-body-primary--bright focus:rounded-md focus:border focus:border-border-color--dark"
               >
                  Skip to main content
               </a>
               <Navigation pathname={pathname} />
               <TransitionPageIn>
                  <main id="main-content" className="max-w-[2500px] mx-auto">{children}</main>
                  <Footer pathname={pathname} />
               </TransitionPageIn>
               <ScrollToTopContainer />
            </div>
         </ErrorBoundary>
      </DarkThemeContext.Provider>
   );
};

Layout.propTypes = {
   children: PropTypes.node.isRequired,
};

export default Layout;
